import { SearchStringFilter } from 'components/SearchStringFilter';
import type { ITableLikeTreeContext, ITableLikeTreeItem } from 'components/TableLikeTree/context';
import { TableLikeTree } from 'components/TableLikeTree/TableLikeTree';
import { TotalCount } from 'components/TotalCount';
import { cnMixSpace, Layout, Tabs, TableChoiceGroupFilter, Button } from 'uikit';
import { isNumber } from 'utils';

import * as R from 'ramda';
import type { CSSProperties, PropsWithChildren, ReactElement } from 'react';
import { useRef, useState, useMemo, useCallback } from 'react';

import type { IContractStructure, TContractStructureItem } from './types';

import Style from './ContractStructure.module.css';

const hierarchyColumnTitle = 'Объект / подобъект';

const getTabItemLabel = (item: { label: string | number }) => item.label;

const filters = [
    {
        id: 'hasGuaranteedVolume',
        name: 'Гарантированный объем: ',
        field: 'hasGuaranteedVolume',
        filterer: R.T,
        component: {
            name: TableChoiceGroupFilter,
            props: {
                items: [
                    {
                        name: 'Да',
                        value: 'Да',
                    },
                    { name: 'Нет', value: 'Нет' },
                ],
            },
        },
    },
    {
        id: 'codeWBS',
        field: 'codeWBS',
        name: 'Код',
        filterer: R.T,
        component: { name: SearchStringFilter },
    },
];

const getParentProp = <T,>(item: TContractStructureItem<T>) => (item.parentId ? String(item.parentId) : 'root');
const groupByParentId = R.groupBy(getParentProp);
const sortByParentBaseOrder = R.sortBy(R.prop('order'));

export const ContractStructure = <T,>(
    props: PropsWithChildren<IContractStructure<T>>,
): ReactElement<any, any> | null => {
    const {
        items,
        constructionTypesTabsItems,
        informers,
        getTagLabel,
        onFiltersUpdated,
        activeConstructionTypeTabItem,
        setActiveConstructionTypeTabItem,
        additionalColumns,
    } = props;

    const [totalCount, setTotalCount] = useState(0);
    const [areAllExpanded, setAreAllExpanded] = useState(true);

    const tableRef = useRef<HTMLDivElement>(null);

    const onTabChange = useCallback(
        ({ value }) => {
            setActiveConstructionTypeTabItem?.(value);
        },
        [setActiveConstructionTypeTabItem],
    );

    const tableTreeItems = useMemo(() => {
        if (items?.length) {
            const groupedByParent = groupByParentId(items);
            const mapper = (o: TContractStructureItem<T>): ITableLikeTreeItem => ({
                ...o,
                id: o.id,
                title: o.name,
                children: sortByParentBaseOrder(groupedByParent[o.id] || [])?.map(mapper),
            });

            return sortByParentBaseOrder(groupedByParent.root || []).map(mapper);
        }

        return [];
    }, [items]);

    const onShownChange = useCallback((count: number) => {
        setTotalCount(count);
    }, []);

    const contextRef = useRef<ITableLikeTreeContext | undefined>();

    return (
        <div
            id="contractStructure"
            style={
                {
                    '--others-height':
                        isNumber(tableRef.current?.getBoundingClientRect?.()?.top) &&
                        (`${(tableRef.current?.getBoundingClientRect?.()?.top ?? 0) + 70}px` as any),
                } as CSSProperties
            }
        >
            <Layout direction="column">
                <Tabs
                    value={activeConstructionTypeTabItem}
                    onChange={onTabChange}
                    items={constructionTypesTabsItems || []}
                    getItemLabel={getTabItemLabel}
                    view="bordered"
                />
            </Layout>
            <TotalCount totalCount={totalCount} className={cnMixSpace({ mT: 'm', mB: 'xs' })} />
            <Layout className={Style.topBar}>
                <Button
                    label="Развернуть все"
                    onClick={() => contextRef?.current?.switchExpandAll?.(true)}
                    view="clear"
                    size="s"
                    disabled={!tableTreeItems.length || areAllExpanded}
                />
                <Button
                    label="Свернуть все"
                    onClick={() => contextRef?.current?.switchExpandAll?.(false)}
                    view="clear"
                    size="s"
                    disabled={!tableTreeItems.length || !areAllExpanded}
                />
            </Layout>
            <div ref={tableRef} className={Style.contractStructureContainer}>
                <TableLikeTree
                    hierarchyColumnTitle={hierarchyColumnTitle}
                    hierarchyColumnFilterTitle={hierarchyColumnTitle}
                    items={tableTreeItems}
                    setContextMenuInHierarchyColumn
                    className={cnMixSpace({ mT: 'xs' })}
                    isRootItemUnableToAppend
                    filters={filters}
                    isFilterEnabled
                    onFiltersUpdated={onFiltersUpdated}
                    getTagLabel={getTagLabel}
                    informers={informers}
                    menuColumnWidth={36}
                    menuColumnAlign="center"
                    additionalColumns={additionalColumns}
                    onShownChange={onShownChange}
                    noMenuColumn
                    contextRef={contextRef}
                    onExpandAllSwitch={setAreAllExpanded}
                />
            </div>
        </div>
    );
};

ContractStructure.displayName = 'ContractStructure';
